import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Row,
  Col,
  Avatar,
  Typography,
  Space,
  Checkbox,
  Modal,
  Form,
  Input,
  Button,
  message,
  Tabs,
  Select,
  Progress,
  Badge,
  Tag,
  Segmented,
} from "antd";
import { Link } from "react-router-dom";
import { EyeOutlined, SearchOutlined, UserOutlined } from "@ant-design/icons";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import { all_routes } from "../../../router/all_routes";
import { useAuthToken } from "../../../../core/data/redux/useAuthToken";
import {
  getAllLeaveTypes,
  postLeaveType,
  putLeaveType,
  LeaveTypeCreate,
  LeaveTypeRecord,
  getAllLeaveBalances,
  LeaveBalanceRecord,
  getDepartmentEmployeeCounts,
  DepartmentEmployeeCount,
} from "../../../../core/api/leaveType";
import useGetEmployees from "../../../../core/api/useGetEmployees";
import { API_BASE_PATH } from "../../../../environment";
import { Pagination } from "antd";
import { usePermission } from "../../../../core/api/useRolePermission";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { RootState } from "../../../../core/data/redux/store";
import { fetchEmployeeImage } from "../../../../core/api/fetchEmployeeImage";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const LeaveSettings: React.FC = () => {
  const empToken = useAuthToken();
  const [empImage, setEmpImage] = useState("/assets/img/defaultavatar.png");
  const empId = useSelector((state: RootState) => state.auth.employeeId);
  const BASE_PATH = API_BASE_PATH;
  // Settings tab states
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeRecord[]>([]);
  const [selectedLeaveType, setSelectedLeaveType] =
    useState<LeaveTypeRecord | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();
  const [modalForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState("settings");

  // Leave Balance tab states
  const [leaveBalances, setLeaveBalances] = useState<LeaveBalanceRecord[]>([]);
  const [departments, setDepartments] = useState<DepartmentEmployeeCount[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string>("ALL");
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  // inside your component function:
  const [rowsPerPage, setRowsPerPage] = useState<number>(7);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Employees
  const { employees, isLoading: loadingEmployees } = useGetEmployees(empToken);
  const [empImages, setEmpImages] = useState<Record<string, string>>({}); // State for employee images

  // Permissions
  const canCreate = usePermission("Leave settings", "create");
  const canUpdate = usePermission("Leave settings", "write");

  const employeeNameMap = useMemo(() => {
    const map: Record<
      string,
      { name: string; department: string; role: string }
    > = {};

    if (!Array.isArray(employees)) return map; // safety check

    employees.forEach((emp) => {
      map[emp.employee_id] = {
        name: `${emp.firstname} ${emp.lastname}`,
        department: emp.department_name || "Unknown Department",
        role: emp.role_name || "Unknown Role",
      };
    });

    return map;
  }, [employees]);

  useEffect(() => {
    if (empId && empToken) {
      const fetchImage = async () => {
        try {
          const timestamp = Date.now();
          const res = await fetch(
            `${API_BASE_PATH}/register/employee/${empId}/image?t=${timestamp}`,
            {
              headers: {
                Authorization: `Bearer ${empToken}`,
              },
            }
          );

          if (!res.ok) throw new Error("Image fetch failed");

          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          setEmpImage(url);
        } catch (err) {
          console.error("Failed to load employee image:", err);
          setEmpImage("/assets/img/defaultavatar.png");
        }
      };

      fetchImage();
    }
  }, [empId, empToken]);

  useEffect(() => {
    const loadImages = async () => {
      const uniqueEmployeeIds = new Set(
        employees.map((emp) => emp.employee_id).filter(Boolean)
      );
      for (const empId of Array.from(uniqueEmployeeIds)) {
        if (!empImages[empId] && empToken) {
          const imgUrl = await fetchEmployeeImage(empId, empToken);
          setEmpImages((prev) => ({ ...prev, [empId]: imgUrl }));
        }
      }
    };
    if (employees.length > 0 && empToken) {
      loadImages();
    }
  }, [employees, empToken]);

  // Merge balances into employees
  const enrichedEmployees = useMemo(() => {
    return employees.map((emp) => {
      const empBalances = leaveBalances.filter(
        (bal) => bal.employee_id === emp.employee_id
      );
      const total_opening_balance = empBalances.reduce(
        (sum, bal) => sum + bal.balance_days, // 🔹 Opening Balance
        0
      );
      const total_availed = empBalances.reduce(
        (sum, bal) => sum + bal.accrued_days, // 🔹 Availed
        0
      );
      const total_leave_balance = empBalances.reduce(
        (sum, bal) => sum + bal.used_days, // 🔹 Leave Balance
        0
      );
      return {
        ...emp,
        balances: {
          total_opening_balance,
          total_availed,
          total_leave_balance,
          details: empBalances,
        },
      };
    });
  }, [employees, leaveBalances]);

  useEffect(() => {
    if (empToken) {
      fetchLeaveTypes();
      fetchLeaveBalances();
      fetchDepartments();
    }
  }, [empToken]);

  const fetchLeaveTypes = async () => {
    try {
      const data = await getAllLeaveTypes(empToken);
      setLeaveTypes(data);
      if (data.length > 0) {
        setSelectedLeaveType(data[0]);
        form.setFieldsValue(data[0]);
      }
    } catch (error) {
      console.error("Error fetching leave types:", error);
    }
  };

  const fetchLeaveBalances = async () => {
    try {
      const data = await getAllLeaveBalances(empToken);
      setLeaveBalances(data);
    } catch (error) {
      console.error("Error fetching leave balances:", error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const data = await getDepartmentEmployeeCounts(empToken);
      setDepartments(data);
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  const handleSave = async () => {
    if (!selectedLeaveType) return;
    try {
      const values = await form.validateFields();
      await putLeaveType(
        { leave_type_id: selectedLeaveType.leave_type_id, ...values },
        empToken
      );
      await fetchLeaveTypes();
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating leave type:", error);
    }
  };

  const handleAdd = async () => {
    try {
      const values = await modalForm.validateFields();
      const payload: LeaveTypeCreate = {
        leave_type_code: values.leave_type_code?.toString().trim(),
        leave_type_name: values.leave_type_name?.toString().trim(),
        max_annual_days: Number(values.max_annual_days || 0),
        is_paid: values.is_paid ?? false,
        carry_forward: values.carry_forward ?? false,
      };

      await postLeaveType(payload, empToken);
      await fetchLeaveTypes();
      setShowModal(false);
      modalForm.resetFields();
    } catch (error) {
      console.error("Error creating leave type:", error);
    }
  };

  const handleSelectLeaveType = (leave: LeaveTypeRecord) => {
    setSelectedLeaveType(leave);
    setIsEditing(false);
    form.setFieldsValue(leave);
  };

  const handleCarryForwardChange = (e: any) => {
    if (e.target.checked) {
      Modal.confirm({
        title: "Carry Forward Confirmation",
        content:
          "Are you sure you want to enable carry forward? This applies only if the user has completed one year.",
        onOk: () => {},
        onCancel: () => {
          modalForm.setFieldValue("carry_forward", false);
        },
      });
    }
  };

  const getDepartmentBadges = () => {
    const departmentColors = [
      "#ff69b4",
      "#00bcd4",
      "#4caf50",
      "#ff9800",
      "#9c27b0",
      "#f44336",
      "#2196f3",
    ];

    return departments.map((dept, index) => (
      <Badge
        key={dept.department_name}
        count={dept.employee_count}
        style={{
          backgroundColor: departmentColors[index % departmentColors.length],
          marginRight: 8,
          marginBottom: 8,
        }}
      >
        <Button
          size="large"
          style={{
            borderRadius: "20px",
            backgroundColor:
              selectedDepartment === dept.department_name ? "#e6f4ff" : "#fff",
            borderColor:
              selectedDepartment === dept.department_name
                ? "#1890ff"
                : "#d9d9d9",
          }}
          onClick={() => setSelectedDepartment(dept.department_name)}
        >
          {dept.department_name}
        </Button>
      </Badge>
    ));
  };

  // Function to filter employees by search and department
  const getFilteredEmployees = () => {
    return employees.filter((emp: any) => {
      const empInfo = employeeNameMap[emp.employee_id];

      // prepare searchable text
      const searchableText = [
        empInfo?.name || "",
        emp.employee_id || "",
        empInfo?.role || "",
        empInfo?.department || "",
      ]
        .join(" ")
        .toLowerCase();

      const matchesSearch = searchableText.includes(searchTerm.toLowerCase());
      const matchesDept =
        selectedDepartment === "ALL" ||
        empInfo?.department === selectedDepartment;

      return matchesSearch && matchesDept;
    });
  };

  // Compute paginated employees
  const paginatedEmployees = useMemo(() => {
    const filtered = getFilteredEmployees();
    const start = (currentPage - 1) * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [employees, searchTerm, selectedDepartment, currentPage, rowsPerPage]);

  const handleViewEmployee = (employee: any) => {
    setSelectedEmployee(employee.employee_id);
    setShowEmployeeModal(true);
  };

  const getSelectedEmployeeData = () => {
    return enrichedEmployees.find(
      (emp) => emp.employee_id === selectedEmployee
    );
  };

  const renderSettingsTab = () => (
    <div>
      <Card
        style={{
          width: "100%",
          height: "auto",
          minHeight: "300px",
          display: "flex",
          flexDirection: "column",
        }}
        bodyStyle={{ padding: 0, flex: 1 }}
      >
        <Row gutter={[0, 0]} style={{ flex: 1 }}>
          <Col
            xs={24}
            sm={24}
            md={8}
            lg={8}
            style={{
              borderRight: "1px solid #f0f0f0",
              height: "100%",
              minHeight: "300px",
            }}
          >
            <div
              style={{
                padding: "16px",
                overflowY: "auto",
                height: "100%",
                maxHeight: "400px",
              }}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                {leaveTypes.map((leave) => {
                  const isSelected =
                    selectedLeaveType?.leave_type_id === leave.leave_type_id;
                  return (
                    <Card
                      key={leave.leave_type_id}
                      onClick={() => handleSelectLeaveType(leave)}
                      style={{
                        cursor: "pointer",
                        backgroundColor: isSelected ? "#f0f6ff" : "#fff",
                        borderColor: isSelected ? "#00ABE9" : "#f0f0f0",
                        transition: "0.3s",
                        marginBottom: "8px",
                      }}
                      size="small"
                      bodyStyle={{ padding: "12px" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <Space>
                          <Avatar
                            style={{
                              backgroundColor: "#e6f4ff",
                              color: "#00ABE9",
                              fontSize: "14px",
                            }}
                            size={32}
                          >
                            {leave.leave_type_code}
                          </Avatar>
                          <div>
                            <Text
                              strong
                              style={{ fontSize: "14px", display: "block" }}
                            >
                              {leave.leave_type_name}
                            </Text>
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              {leave.max_annual_days} days •{" "}
                              {leave.is_paid ? "Paid" : "Unpaid"}
                            </Text>
                          </div>
                        </Space>
                        <EyeOutlined
                          style={{ color: "#999", fontSize: "14px" }}
                        />
                      </div>
                    </Card>
                  );
                })}
              </Space>
            </div>
          </Col>
          <Col
            xs={24}
            sm={24}
            md={16}
            lg={16}
            style={{
              height: "100%",
              minHeight: "300px",
              padding: "16px",
            }}
          >
            {selectedLeaveType ? (
              <>
                <Card
                  size="small"
                  style={{
                    marginBottom: "24px",
                    backgroundColor: "#b3e8faff",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: "12px",
                    }}
                  >
                    <Space>
                      <Avatar
                        size={40}
                        style={{
                          backgroundColor: "#e6f4ff",
                          color: "#00ABE9",
                          fontWeight: "bold",
                        }}
                      >
                        {selectedLeaveType.leave_type_code}
                      </Avatar>
                      <div>
                        <Title
                          level={4}
                          style={{ margin: 0, fontSize: "18px" }}
                        >
                          {selectedLeaveType.leave_type_name}
                        </Title>
                        <Text type="secondary" style={{ fontSize: "14px" }}>
                          Leave Type Details
                        </Text>
                      </div>
                    </Space>
                    <i
                      className="ti ti-edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!canUpdate) {
                          toast.warning(
                            "You don't have permission to edit leave types."
                          );
                          return;
                        }
                        setIsEditing(true);
                      }}
                      style={{ cursor: "pointer", fontSize: "18px" }}
                    />
                  </div>
                </Card>
                <Form
                  form={form}
                  layout="vertical"
                  initialValues={selectedLeaveType}
                  disabled={!isEditing}
                  style={{ width: "100%" }}
                >
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                      <Form.Item label="Leave Type Code" name="leave_type_code">
                        <Input style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item label="Leave Type Name" name="leave_type_name">
                        <Input style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item label="Max Annual Days" name="max_annual_days">
                        <Input type="number" style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name="carry_forward" valuePropName="checked">
                        <Checkbox>Carry Forward</Checkbox>
                      </Form.Item>
                    </Col>
                    {isEditing && (
                      <Col xs={24} style={{ textAlign: "right" }}>
                        <Button type="primary" onClick={handleSave}>
                          Save Changes
                        </Button>
                      </Col>
                    )}
                  </Row>
                </Form>
              </>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  minHeight: "200px",
                }}
              >
                <Text type="secondary" style={{ fontSize: "16px" }}>
                  Select a leave type to view details
                </Text>
              </div>
            )}
          </Col>
        </Row>
      </Card>
    </div>
  );

  const renderLeaveBalanceTab = () => (
    <div>
      {/* Organization Section - Option 2 */}
      <Card className="shadow-sm mb-4">
        <div className="mb-3">
          <Title level={4}>Organization</Title>
          <Segmented
            options={["ALL", ...departments.map((d) => d.department_name)]}
            value={selectedDepartment}
            onChange={(val) => setSelectedDepartment(val as string)} // 👈 cast to string
            style={{ width: "100%", background: "#dcdadaff", borderRadius: 8 }}
          />
        </div>
      </Card>

      {/* Employee List */}
      <Card className="shadow-sm">
        {/* Top Controls: Row Per Page + Search */}
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: "16px" }}
        >
          <Col>
            <Space>
              <Text strong>Row Per Page</Text>
              <Select
                value={rowsPerPage}
                onChange={(val) => setRowsPerPage(val)}
                style={{ width: 80 }}
                options={[
                  { value: 7, label: "7" },
                  { value: 14, label: "14" },
                  { value: 21, label: "21" },
                  { value: 30, label: "30" },
                ]}
              />
              <Text>Entries</Text>
            </Space>
          </Col>
          <Col>
            <Input.Search
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 300 }}
            />
          </Col>
        </Row>
        {/* Employee List */}
        <div style={{ minHeight: "400px" }}>
          {paginatedEmployees.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "60px 0",
                backgroundColor: "#f5f5f5",
                borderRadius: "10px",
              }}
            >
              <Text style={{ color: "black", fontSize: "18px" }}>
                No Data Found
              </Text>
            </div>
          ) : (
            <Space direction="vertical" style={{ width: "100%" }}>
              {paginatedEmployees.map((employee: any) => {
                const empInfo = employeeNameMap[employee.employee_id];
                const imageUrl =
                  empImages[employee.employee_id] ||
                  "/assets/img/defaultavatar.png";
                return (
                  <Card
                    key={employee.employee_id}
                    className="shadow-md"
                    style={{ backgroundColor: "#f5f5f5" }}
                    bodyStyle={{ padding: "16px" }}
                  >
                    <Row align="middle" justify="space-between">
                      <Col span={4}>
                        <div className="d-flex align-items-center">
                          <Avatar
                            src={imageUrl}
                            icon={<UserOutlined />}
                            size={40}
                          />
                          <div className="ms-2">
                            <Text
                              strong
                              style={{ color: "black", fontSize: "16px" }}
                            >
                              {empInfo?.name || employee.employee_id}
                            </Text>
                            <br />
                            <Text style={{ color: "black", fontSize: "12px" }}>
                              {employee.employee_id}
                            </Text>
                          </div>
                        </div>
                      </Col>
                      <Col span={4}>
                        <Text style={{ fontSize: "12px" }}>Department</Text>
                        <br />
                        <Text strong>{empInfo?.department}</Text>
                      </Col>
                      <Col span={4}>
                        <Text style={{ fontSize: "12px" }}>Role</Text>
                        <br />
                        <Text strong>{empInfo?.role}</Text>
                      </Col>
                      <Col span={3}>
                        <Text style={{ fontSize: "12px" }}>
                          Opening Balance
                        </Text>
                        <br />
                        <Text strong>
                          {employee.balances?.total_opening_balance ?? 0}
                        </Text>
                      </Col>
                      <Col span={3}>
                        <Text style={{ fontSize: "12px" }}>Availed</Text>
                        <br />
                        <Text strong>
                          {employee.balances?.total_availed ?? 0}
                        </Text>
                      </Col>
                      <Col span={3}>
                        <Text style={{ fontSize: "12px" }}>Used Days</Text>
                        <br />
                        <Text strong>
                          {employee.balances?.total_leave_balance ?? 0}
                        </Text>
                      </Col>
                      <Col span={3}>
                        <Button
                          shape="circle"
                          style={{ backgroundColor: "#1763AD", border: "none" }}
                          icon={<EyeOutlined style={{ color: "white" }} />}
                          onClick={() => handleViewEmployee(employee)}
                        />
                      </Col>
                    </Row>
                  </Card>
                );
              })}
            </Space>
          )}
        </div>

        {/* Pagination */}
        <Row justify="end" style={{ marginTop: "16px" }}>
          <Pagination
            current={currentPage}
            pageSize={rowsPerPage}
            total={getFilteredEmployees().length}
            onChange={(page) => setCurrentPage(page)}
            showSizeChanger={false}
          />
        </Row>
      </Card>
    </div>
  );
  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-0">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Leave Settings</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Leave Settings
                </li>
              </ol>
            </nav>
          </div>
        </div>

        {/* Tab Navigation Row with Buttons */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto">
            <Tabs activeKey={activeTab} onChange={setActiveTab} size="large">
              <TabPane tab="Settings" key="settings" />
              <TabPane tab="Leave Balance" key="balance" />
            </Tabs>
          </div>

          {/* Right side buttons - only show when Settings tab is active */}
          {activeTab === "settings" && (
            <div className="head-icons d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center"
                onClick={(e) => {
                  if (!canCreate) {
                    toast.warning(
                      "You don't have permission to add leave types."
                    );
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                  }
                  modalForm.resetFields();
                  setShowModal(true);
                }}
              >
                <i className="ti ti-circle-plus me-2" />
                Add Leave Type
              </button>
              <CollapseHeader />
            </div>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === "settings" && renderSettingsTab()}
        {activeTab === "balance" && renderLeaveBalanceTab()}
      </div>

      {/* Add Leave Type Modal */}
      <Modal
        title="Add Leave Type"
        open={showModal}
        onCancel={() => {
          setShowModal(false); // just close
          modalForm.resetFields(); // optional: also reset when closing
        }}
        footer={[
          <Button
            key="reset"
            onClick={() => modalForm.resetFields()} // ✅ Reset form
          >
            Reset
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleAdd} // ✅ Submit
          >
            Submit
          </Button>,
        ]}
      >
        <Form layout="vertical" form={modalForm}>
          <Form.Item
            label="Leave Type Code"
            name="leave_type_code"
            rules={[{ required: true, message: "Please enter code" }]}
          >
            <Input placeholder="Enter code" />
          </Form.Item>

          <Form.Item
            label="Leave Type Name"
            name="leave_type_name"
            rules={[{ required: true, message: "Please enter name" }]}
          >
            <Input placeholder="Enter name" />
          </Form.Item>

          <Form.Item label="Max Annual Days" name="max_annual_days">
            <Input type="number" placeholder="Enter number" />
          </Form.Item>

          <Form.Item
            name="carry_forward"
            valuePropName="checked"
            initialValue={false}
          >
            <Checkbox>Carry Forward</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      {/* Employee Profile Custom Modal */}
      {showEmployeeModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowEmployeeModal(false)}
        >
          <div
            style={{
              width: 800,
              backgroundColor: "#fff",
              borderRadius: 12,
              boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
              overflow: "hidden",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                background: "#1e3a8a",
                color: "white",
                padding: "16px 20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                minHeight: 56,
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 600,
                  color: "white",
                }}
              >
                Employee Profile
              </h3>
              <button
                onClick={() => setShowEmployeeModal(false)}
                style={{
                  background: "rgba(255, 255, 255, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: "50%",
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "white",
                  fontSize: 18,
                  transition: "all 0.3s ease",
                  backdropFilter: "blur(10px)",
                }}
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div
              style={{
                padding: "24px",
                overflowY: "auto",
                flex: 1,
              }}
            >
              {selectedEmployee &&
                (() => {
                  const empData = getSelectedEmployeeData();
                  if (!empData) return null;

                  const empInfo = employeeNameMap[empData.employee_id];
                  const imageUrl = `${BASE_PATH}/register/employee/${empData.employee_id}/image`;

                  const opening = empData.balances?.total_opening_balance ?? 0;
                  const availed = empData.balances?.total_availed ?? 0;
                  const balance = empData.balances?.total_leave_balance ?? 0;

                  return (
                    <div>
                      {/* Employee Info (matches screenshot) */}
                      <div
                        style={{
                          background: "#edf2f7", // light grey
                          borderRadius: 10,
                          padding: "16px 20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 20,
                        }}
                      >
                        {/* Left: Avatar + name/id */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                          }}
                        >
                          <Avatar
                            size={48}
                            src={imageUrl}
                            icon={<UserOutlined />}
                          />
                          <div>
                            <div
                              style={{
                                fontWeight: 700,
                                fontSize: 16,
                                textTransform: "capitalize",
                                color: "#111827",
                              }}
                            >
                              {empInfo?.name || empData.employee_id}
                            </div>
                            <div style={{ fontSize: 12, color: "#6b7280" }}>
                              {empData.employee_id}
                            </div>
                          </div>
                        </div>

                        {/* Middle: Department & Role */}
                        <div
                          style={{
                            display: "flex",
                            gap: 32,
                            flex: 1,
                            justifyContent: "center",
                          }}
                        >
                          <div>
                            <div style={{ fontSize: 12, color: "#6b7280" }}>
                              Department
                            </div>
                            <div style={{ fontWeight: 600, color: "#111827" }}>
                              {empInfo?.department || "-"}
                            </div>
                          </div>
                        </div>

                        {/* Right: Opening / Availed / Balance */}
                        <div style={{ display: "flex", gap: 32 }}>
                          <div>
                            <div>
                              <div style={{ fontSize: 12, color: "#6b7280" }}>
                                Role
                              </div>
                              <div
                                style={{ fontWeight: 600, color: "#111827" }}
                              >
                                {empInfo?.role || "-"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Leave Balance */}
                      <div style={{ marginTop: "24px" }}>
                        <h4
                          style={{
                            marginBottom: "16px",
                            fontWeight: 600,
                            color: "#1e3a8a",
                          }}
                        >
                          Leave Balance
                        </h4>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "20px",
                            justifyContent: "center",
                          }}
                        >
                          {empData.balances?.details?.map(
                            (bal: LeaveBalanceRecord, index) => {
                              const leaveType = leaveTypes.find(
                                (lt) => lt.leave_type_id === bal.leave_type_id
                              );

                              const colors = [
                                "#1890ff",
                                "#52c41a",
                                "#faad14",
                                "#eb2f96",
                              ];
                              const color = colors[index % colors.length];

                              return (
                                <div
                                  key={bal.leave_type_id}
                                  style={{
                                    textAlign: "center",
                                    background: "#f9f9f9",
                                    padding: "16px",
                                    borderRadius: "12px",
                                    width: 150,
                                    boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                                  }}
                                >
                                  <Progress
                                    type="circle"
                                    percent={100}
                                    format={() =>
                                      bal.balance_days === 0
                                        ? bal.accrued_days
                                        : bal.balance_days
                                    }
                                    strokeColor={color}
                                    size={80}
                                  />
                                  <div
                                    style={{
                                      marginTop: "10px",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {leaveType?.leave_type_name || "Unknown"}
                                  </div>
                                </div>
                              );
                            }
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
            </div>
          </div>
        </div>
      )}
      {/* Footer */}
      <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
        <p className="mb-0">2024 - 2025 © IATT.</p>
        <p>
          Designed & Developed By{" "}
          <Link to="#" className="text-primary">
            IATT
          </Link>
        </p>
      </div>
    </div>
  );
};
export default LeaveSettings;
